import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { FiDialogsContentBase } from '@fi-shared/fi-dialogs/fi-dialogs-content-base';
import { Subject } from 'rxjs';
import { ICheckpointAction, ICheckpointActionAlertRecipient } from '@fi-shared/checklist/checklist-v2';
import {
    FiToggleSwitchLabelAlign,
    FiToggleSwitchSliderColorClass,
} from '@fi-shared/fi-toggle-switch/fi-toggle-switch.component';
import { IFiDialogResult } from '@fi-shared/fi-dialogs/fi-dialogs';
import { FiSnackBarService } from '@fi-shared/fi-snackbar/snack-bar.service';
import { AppTextService } from '@fi-shared/app-text';
import { IAccessRightFilterCollection } from '@fi-shared/models';
import { CheckpointActionType } from '@fi-shared/checklist/checklist-enum';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { FiValuesComparison } from '@fi-shared/utils/fi-values-comparison';
import { FiCheckpoint2HelperService } from '@fi-shared/checklist/fi-checkpoint2-helper.service';
import { FiChecklistModuleDiscardChangesDialogData } from '@fi-app/shared/checklist/fi-checklist-helper.service';
import { ObjectTypes } from '@fi-shared/types';

export interface IComplianceThresholdDialogData {
    isComplianceThresholdEnabled: boolean;
    complianceThreshold: number;
    complianceThresholdActions: ICheckpointAction[];
    accessRightFilterCollection: IAccessRightFilterCollection;
    checkpointName: string;
    checklistName: string;
}

export interface IComplianceThresholdDialogResult {
    isComplianceThresholdEnabled: boolean;
    complianceThreshold: number;
    complianceThresholdActions: ICheckpointAction[];
}

@Component({
    selector: 'fi-compliance-threshold-dialog',
    templateUrl: './compliance-threshold-dialog.component.html',
    styleUrl: './compliance-threshold-dialog.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-compliance-threshold-dialog' },
    standalone: false,
})
export class FiComplianceThresholdDialogComponent
    extends FiDialogsContentBase<IComplianceThresholdDialogData, IComplianceThresholdDialogResult>
    implements OnInit, OnDestroy
{
    get complianceThresholdActions() {
        return this.data.complianceThresholdActions;
    }

    private fiCheckpoint2HelperService: FiCheckpoint2HelperService;
    private fiSnackbarService: FiSnackBarService;
    private appTextService: AppTextService;
    private cdr: ChangeDetectorRef;
    private fiDialogsService: FiDialogsService;

    protected readonly ComplianceThresholdValues = [70, 75, 80, 85, 90, 95, 100];
    private fiValuesComparison: FiValuesComparison<IComplianceThresholdDialogData>;

    ngOnInit() {
        this.setServices();
        this.fiValuesComparison = new FiValuesComparison(() => this.data);
        this.subscribeOnTransitionCloseDialog(this.fiDialogsService, this.ngOnDestroy$);
    }

    onComplianceThresholdEnabledChanged() {
        this.data.complianceThreshold = this.data.isComplianceThresholdEnabled ? 100 : undefined;
    }

    getResult(): IComplianceThresholdDialogResult {
        return {
            isComplianceThresholdEnabled: this.data.isComplianceThresholdEnabled,
            complianceThreshold: this.data.complianceThreshold,
            complianceThresholdActions: this.data.complianceThresholdActions,
        };
    }

    openAddNewCheckpointAction(actionType: CheckpointActionType) {
        const newCheckpointAction: ICheckpointAction = this.fiCheckpoint2HelperService.getDefaultAction(
            actionType,
            true,
        );

        this.getCheckpointActionEditorDialog(newCheckpointAction).onYesButtonClick(
            (newCheckpointAction: ICheckpointAction) => {
                this.complianceThresholdActions.push(newCheckpointAction);
                this.cdr.markForCheck();
            },
        );
    }

    editCheckpointAction(actionIndex: number) {
        this.getCheckpointActionEditorDialog(this.complianceThresholdActions[actionIndex]).onYesButtonClick(
            (newCheckpointAction: ICheckpointAction) => {
                this.complianceThresholdActions[actionIndex] = newCheckpointAction;
                this.cdr.markForCheck();
            },
        );
    }

    openConfirmDeleteActionDialog(actionIndex: number) {
        this.fiCheckpoint2HelperService
            .getConfirmDeleteCheckpointActionsDialog()
            .onYesButtonClick(() => this.deleteAction(actionIndex));
    }

    getSelectedItemIds(alertRecipient: ICheckpointActionAlertRecipient): number[] {
        return this.fiCheckpoint2HelperService.getCheckpointTaskRecipientIds(alertRecipient);
    }

    private getCheckpointActionEditorDialog(checkpointAction: ICheckpointAction): IFiDialogResult<ICheckpointAction> {
        return this.fiCheckpoint2HelperService.openCheckpointNotificationActionEditorDialog(
            checkpointAction,
            this.data.accessRightFilterCollection,
            true,
            this.data.checkpointName,
            this.data.checklistName,
        );
    }

    private deleteAction(actionIndex: number) {
        this.complianceThresholdActions.splice(actionIndex, 1);
        this.cdr.markForCheck();
        this.fiSnackbarService.showSnackbar({
            textMessage: this.appTextService.get('checklist.checkpoint_action_deleted'),
        });
    }

    getNoButtonClickPromise(): Promise<{ canClose: boolean }> {
        return this.fiDialogsService.getCanCloseConfirmation(!this.fiValuesComparison.isChanged());
    }

    private setServices() {
        this.fiCheckpoint2HelperService = this.injector.get(FiCheckpoint2HelperService);
        this.fiSnackbarService = this.injector.get(FiSnackBarService);
        this.appTextService = this.injector.get(AppTextService);
        this.cdr = this.injector.get(ChangeDetectorRef);
        this.fiDialogsService = this.injector.get(FiDialogsService);
    }

    private ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    protected readonly FiToggleSwitchLabelAlign = FiToggleSwitchLabelAlign;
    protected readonly CheckpointActionType = CheckpointActionType;
    protected readonly FiToggleSwitchSliderColorClass = FiToggleSwitchSliderColorClass;
    protected readonly ObjectTypes = ObjectTypes;
}
